/**
 * Body lines for the Animation Engine page.
 * Face fragments stay on the expression catalog.
 * Keep src/illustration/animationBodies.json aligned with
 * functions/illustration/animation/bodies.json.
 *
 * Person and dog tracks are separate. fear.scared exists on both,
 * and the two sentences do not match.
 * The joint note and the ethogram note stay on the cell.
 * animationCopyLine prints Face, Body, and Effort only.
 */

import BODIES from './animationBodies.json';

export const ANIMATION_SOURCES = [
  {
    role: 'People',
    title: 'Body Action and Posture',
    body: 'Dael, Mortillaro, and Scherer built BAP so a body could be recorded by which part moved, and which way. A filled cell follows that rule, and we do not use their code numbers. The patterns come from Wallbott’s study of actors’ bodies, and from the same authors’ later coding of the GEMEP corpus: how much the body moves, and which way. A cell with no such pattern is still written. Where placing that pattern took a guess, the assumption is recorded in the spec, not on the card.',
    cite: 'Wallbott, H. G. (1998). Bodily expression of emotion. European Journal of Social Psychology, 28(6), 879–896. Dael, N., Mortillaro, M., & Scherer, K. R. (2012). Emotion expression in body action and posture. Emotion, 12(5), 1085–1101. Dael, N., Mortillaro, M., & Scherer, K. R. (2012). The Body Action and Posture coding system (BAP): Development and reliability. Journal of Nonverbal Behavior, 36(2), 97–121.',
  },
  {
    role: 'How it moves',
    title: 'Laban Effort',
    body: 'Laban described the feel of a movement in four words: space, weight, time, and flow. On a filled cell we print our reading of that movement, in that order. There is no emotion-to-effort table in Laban, and an empty cell has no effort words.',
    cite: 'Laban, R., & Lawrence, F. C. (1947). Effort. Macdonald & Evans.',
  },
  {
    role: 'Dogs',
    title: 'Ethogram',
    body: 'Beerda and colleagues recorded a lowered posture and a low or tucked tail when a dog was acutely stressed. Bradshaw and Nott describe a high, loose tail as affiliative, and a high, stiff tail with the weight forward as threat. Schenkel described submission as a lowered body and a turned head. Bekoff’s play bow is forequarters down and hindquarters up, and that bow is not one of these cells, so it does not fill this matrix.',
    cite: 'Beerda, B., Schilder, M. B. H., van Hooff, J. A. R. A. M., & de Vries, H. W. (1997). Manifestations of chronic and acute stress in dogs. Applied Animal Behaviour Science, 52, 307–319. Bradshaw, J., & Nott, H. (1995). Social and communication behaviour of companion dogs. In J. Serpell (Ed.), The domestic dog. Cambridge University Press. Schenkel, R. (1967). Submission: Its features and function in the wolf and dog. American Zoologist, 7(2), 319–329. Bekoff, M. (1974). Social play and play-soliciting by infant canids. American Zoologist, 14(1), 323–340.',
  },
];

export const ANIMATION_CATALOG_VERSION = '0.3.0-draft';

export const ANIMATION_BODIES = BODIES;

export function bodyFor(id, kind) {
  const track = kind === 'dog' ? BODIES.dog : BODIES.person;
  return (track && track[id]) || null;
}

export function effortWords(effort) {
  if (!effort) return '';
  return [effort.space, effort.weight, effort.time, effort.flow].filter(Boolean).join(', ');
}

function closed(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/** The line a vignette prompt prints for one cell. Empty until the body is written. */
export function animationCopyLine(cell, body) {
  if (!cell || !body?.fragment) return '';
  return `${cell.label} — Face: ${closed(cell.fragment)} Body: ${closed(body.fragment)} Effort: ${effortWords(body.effort)}.`;
}
